import { useCallback, useEffect, useRef } from "react";
import { newFrameId } from "../lib/frame";

type SendRealtimeCommand = (type: string, payload: Record<string, unknown>, id?: string) => boolean;

type UseTypingPresenceArgs = {
  conn: "disconnected" | "connecting" | "open" | "closed";
  setChatInput: (value: string) => void;
  sendRealtimeCommand: SendRealtimeCommand;
};

const TYPING_IDLE_MS = 3000;
const TYPING_THROTTLE_MS = 400;

export function useTypingPresence({ conn, setChatInput, sendRealtimeCommand }: UseTypingPresenceArgs) {
  const typingIdleTimerRef = useRef<number | null>(null);
  const typingThrottleRef = useRef<number | null>(null);
  const lastTypingSentRef = useRef(0);

  useEffect(() => {
    return () => {
      if (typingIdleTimerRef.current) window.clearTimeout(typingIdleTimerRef.current);
      if (typingThrottleRef.current) window.clearTimeout(typingThrottleRef.current);
    };
  }, []);

  const onChatInput = useCallback(
    (value: string) => {
      setChatInput(value);
      if (conn !== "open") return;

      if (typingThrottleRef.current) window.clearTimeout(typingThrottleRef.current);
      if (value.length > 0) {
        typingThrottleRef.current = window.setTimeout(() => {
          const now = Date.now();
          if (now - lastTypingSentRef.current >= TYPING_THROTTLE_MS) {
            sendRealtimeCommand("presence.update", { status: "typing" }, newFrameId());
            lastTypingSentRef.current = now;
          }
          typingThrottleRef.current = null;
        }, 120);
      }

      if (typingIdleTimerRef.current) window.clearTimeout(typingIdleTimerRef.current);
      if (value.length > 0) {
        typingIdleTimerRef.current = window.setTimeout(() => {
          sendRealtimeCommand("presence.update", { status: "idle" }, newFrameId());
        }, TYPING_IDLE_MS);
      } else {
        sendRealtimeCommand("presence.update", { status: "idle" }, newFrameId());
      }
    },
    [conn, sendRealtimeCommand, setChatInput],
  );

  return { onChatInput };
}
